function formatUpsellMoney(cents, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: currency || 'USD',
    trailingZeroDisplay: 'stripIfInteger'
  }).format(cents / 100);
}

class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-upsell-variants]')?.textContent || '[]');
    this.variantImages = JSON.parse(this.querySelector('[data-upsell-variant-images]')?.textContent || '{}');
    this.imageEl = this.querySelector('[data-upsell-image]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.comparePriceEl = this.querySelector('[data-upsell-compare-price]');
    this.salePriceEl = this.querySelector('[data-upsell-sale-price]');
    this.submitButton = this.querySelector('.cart-drawer-upsell__submit');
    this.currency = this.dataset.currency || 'USD';
    this.previewType = this.dataset.previewType || 'none';
    this.previewValue = parseFloat(this.dataset.previewValue || '0');

    this.querySelectorAll('[data-option-index]').forEach((select) => {
      select.addEventListener('change', (event) => this.onOptionChange(event));
    });

    this.updateDependentOptions();
    this.syncVariant();
  }

  onOptionChange(event) {
    const changedSelect = event?.target?.matches?.('[data-option-index]') ? event.target : null;
    const changedIndex = changedSelect ? parseInt(changedSelect.dataset.optionIndex, 10) : -1;

    if (changedSelect) {
      const display = this.querySelector(`[data-option-display="${changedSelect.dataset.optionIndex}"]`);
      if (display) {
        display.textContent = changedSelect.value;
      }
    }

    this.updateDependentOptions(changedIndex);
    this.syncVariant();
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-index]'))
      .sort((a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10))
      .map((select) => select.value);
  }

  getAvailableValuesForOption(optionIndex, partialSelection) {
    const matchingVariants = this.variants.filter((variant) => {
      const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return partialSelection.every((value, index) => opts[index] === value);
    });

    const optionKey = `option${optionIndex + 1}`;
    return [...new Set(matchingVariants.map((variant) => variant[optionKey]).filter(Boolean))];
  }

  updateSelectOptions(select, values) {
    if (values.length === 0) {
      return select.value;
    }

    const currentValue = select.value;
    const newValue = values.includes(currentValue) ? currentValue : values[0];

    select.replaceChildren(
      ...values.map((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        option.selected = value === newValue;
        return option;
      })
    );

    return newValue;
  }

  updateDependentOptions(changedIndex = -1) {
    const selects = Array.from(this.querySelectorAll('[data-option-index]')).sort(
      (a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10)
    );

    if (selects.length === 0) {
      return;
    }

    const selectedOptions = selects.map((select) => select.value);

    for (let optionIndex = changedIndex + 1; optionIndex < selects.length; optionIndex += 1) {
      const partialSelection = selectedOptions.slice(0, optionIndex);
      const availableValues = this.getAvailableValuesForOption(optionIndex, partialSelection);
      const newValue = this.updateSelectOptions(selects[optionIndex], availableValues);
      selectedOptions[optionIndex] = newValue;

      const display = this.querySelector(`[data-option-display="${selects[optionIndex].dataset.optionIndex}"]`);
      if (display) {
        display.textContent = newValue;
      }
    }
  }

  findVariant() {
    const options = this.getSelectedOptions();

    return this.variants.find((variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return variantOptions.every((value, index) => value === options[index]);
    });
  }

  calculatePreviewPrice(variantPrice) {
    if (this.previewType === 'percentage' && this.previewValue > 0) {
      return Math.max(0, Math.round(variantPrice * (1 - this.previewValue / 100)));
    }

    if (this.previewType === 'fixed_amount' && this.previewValue > 0) {
      return Math.max(0, variantPrice - Math.round(this.previewValue * 100));
    }

    return variantPrice;
  }

  updatePriceDisplay(variant) {
    if (!this.salePriceEl || !variant) {
      return;
    }

    const previewPrice = this.calculatePreviewPrice(variant.price);
    const showPreview = previewPrice < variant.price;

    this.salePriceEl.textContent = formatUpsellMoney(showPreview ? previewPrice : variant.price, this.currency);

    if (this.comparePriceEl) {
      if (showPreview || variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = formatUpsellMoney(showPreview ? variant.price : variant.compare_at_price, this.currency);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }
  }

  updateImageDisplay(variant) {
    if (!this.imageEl || !variant) {
      return;
    }

    const imageData = this.variantImages[String(variant.id)];
    if (!imageData?.src) {
      return;
    }

    this.imageEl.src = imageData.src;
    if (imageData.srcset) {
      this.imageEl.srcset = imageData.srcset;
    } else {
      this.imageEl.removeAttribute('srcset');
    }

    if (imageData.sizes) {
      this.imageEl.sizes = imageData.sizes;
    }
  }

  syncVariant() {
    const variant = this.findVariant() || this.variants.find((entry) => entry.available) || this.variants[0];

    if (!variant || !this.variantInput) {
      return;
    }

    this.variantInput.value = variant.id;
    this.updatePriceDisplay(variant);
    this.updateImageDisplay(variant);

    if (this.submitButton) {
      this.submitButton.disabled = !variant.available;
      this.submitButton.textContent = variant.available
        ? (this.submitButton.dataset.availableLabel || 'Add to cart')
        : (this.submitButton.dataset.soldOutLabel || 'Sold out');
    }
  }

}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}

/**
 * Upsell lines must stay at their discounted qty.
 * Extra units are added as a separate line without the _upsell property.
 */
async function addNonUpsellUnits(variantId, quantity) {
  const sectionsToBundle = new Set();
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
    bubbles: true,
    detail: { sections: sectionsToBundle }
  }));

  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  try {
    const item = {
      id: Number(variantId),
      quantity
    };

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [item],
        sections: [...sectionsToBundle].join(',')
      })
    });

    const responseJson = await response.json();
    if (!response.ok) {
      throw new Error(responseJson.description || responseJson.message || 'Could not update cart');
    }

    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent.sections = responseJson.sections;

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'line-item:change',
        cart: cartContent
      }
    }));
  } finally {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  }
}

function getUpsellLineCurrentQuantity(lineItem) {
  return parseInt(lineItem.querySelector('[data-line-key]')?.value || '1', 10);
}

function handleUpsellQuantityIncrease(lineItem, targetQuantity, currentQuantity = getUpsellLineCurrentQuantity(lineItem)) {
  const delta = targetQuantity - currentQuantity;
  const variantId = lineItem.dataset.variantId;

  if (delta <= 0 || !variantId) {
    return false;
  }

  addNonUpsellUnits(variantId, delta)
    .catch((error) => {
      console.error('Upsell quantity update error:', error);
      lineItem.querySelector('quantity-selector')?.restoreDefaultValue?.();
    });

  return true;
}

document.addEventListener('focusin', (event) => {
  const input = event.target.closest('line-item[data-upsell-line] [data-line-key]');
  if (input) {
    input.dataset.upsellPrevQty = input.value;
  }
}, true);

document.addEventListener('click', (event) => {
  const link = event.target.closest(`a[href*="${Shopify.routes.root}cart/change"]`);
  if (!link) {
    return;
  }

  const lineItem = link.closest('line-item[data-upsell-line]');
  if (!lineItem) {
    return;
  }

  const url = new URL(link.href, window.location.origin);
  const targetQuantity = parseInt(url.searchParams.get('quantity'), 10);
  const currentQuantity = getUpsellLineCurrentQuantity(lineItem);

  if (Number.isNaN(targetQuantity) || targetQuantity <= currentQuantity) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  handleUpsellQuantityIncrease(lineItem, targetQuantity, currentQuantity);
}, true);

document.addEventListener('change', (event) => {
  const input = event.target.closest('line-item[data-upsell-line] [data-line-key]');
  if (!input) {
    return;
  }

  const lineItem = input.closest('line-item[data-upsell-line]');
  const previousQuantity = parseInt(input.dataset.upsellPrevQty || input.defaultValue || '1', 10);
  const targetQuantity = parseInt(input.value, 10);

  if (!lineItem || Number.isNaN(targetQuantity) || targetQuantity <= previousQuantity) {
    return;
  }

  event.stopImmediatePropagation();
  input.value = String(previousQuantity);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  handleUpsellQuantityIncrease(lineItem, targetQuantity, previousQuantity);
}, true);
